import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

/**
 * Single source of truth for the FHNW "special" schemaForm field factories
 * (contact_allowed, data-protection notice, phone number).
 *
 * Both the normal schemaForm block (`config/schemaFormBlock`) and the
 * event-registration form defaults (`Blocks/EventRegistrationForm/defaults`)
 * build these fields from the same builders so the widget / choices / static
 * text — and their translations — never drift apart.
 *
 * The message ids deliberately match the strings used historically in
 * `schemaFormBlock` so the existing translation catalog entries keep applying.
 */

export type FieldFactoryInitialData = {
  type: string;
  factory: string;
  id?: string;
  title?: string;
  widget?: string;
  choices?: [string, string][];
  default?: { data: string };
};

export const specialFormFieldMessages = defineMessages({
  contactAllowed: {
    id: 'contactAllowed',
    defaultMessage: 'Contact allowed',
  },
  contactAllowedTitle: {
    id: 'We would like to tell you about what is happening at FHNW in the future but require your consent to do so.',
    defaultMessage:
      'We would like to tell you about what is happening at FHNW in the future but require your consent to do so.',
  },
  contactAllowedYes: {
    id: 'I would like to receive more information',
    defaultMessage: 'I would like to receive more information',
  },
  contactAllowedNo: {
    id: 'I would not like to receive any more information',
    defaultMessage: 'I would not like to receive any more information',
  },
  dataprotectionInfo: {
    id: 'dataprotectionInfo',
    defaultMessage: 'Data protection info',
  },
  dataprotectionInfoValue: {
    id: '<p><strong>Data privacy</strong></p><p>Without your further consent, the data sent will only be used for the purposes of processing your request. You can find more information on the <a href="https://www.fhnw.ch/en/imprint/data-protection" target="_blank" rel="noopener noreferrer" class="external">Legal page</a>.</p>',
    defaultMessage:
      '<p><strong>Data privacy</strong></p><p>Without your further consent, the data sent will only be used for the purposes of processing your request. You can find more information on the <a href="https://www.fhnw.ch/en/imprint/data-protection" target="_blank" rel="noopener noreferrer" class="external">Legal page</a>.</p>',
  },
  phonenumber: {
    id: 'phonenumber',
    defaultMessage: 'Phone number',
  },
});

export const buildContactAllowedInitialData = (
  intl: IntlShape,
): FieldFactoryInitialData => ({
  type: 'string',
  factory: 'contactAllowed',
  title: intl.formatMessage(specialFormFieldMessages.contactAllowedTitle),
  widget: 'radio_group',
  choices: [
    [
      intl.formatMessage(specialFormFieldMessages.contactAllowedYes),
      intl.formatMessage(specialFormFieldMessages.contactAllowedYes),
    ],
    [
      intl.formatMessage(specialFormFieldMessages.contactAllowedNo),
      intl.formatMessage(specialFormFieldMessages.contactAllowedNo),
    ],
  ],
  id: 'contact_allowed',
});

export const buildDataprotectionInfoInitialData = (
  intl: IntlShape,
): FieldFactoryInitialData => ({
  type: 'object',
  factory: 'dataprotectionInfo',
  widget: 'static_text',
  default: {
    data: intl.formatMessage(specialFormFieldMessages.dataprotectionInfoValue),
  },
  id: 'dataprotectioninfo',
});

export const buildPhonenumberInitialData = (
  intl: IntlShape,
): FieldFactoryInitialData => ({
  type: 'string',
  factory: 'phonenumber',
  id: 'phonenumber',
});
